import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { Button } from "@/ds/shadcn/components/ui/button";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { ListItem, ListItemMenu } from "@/features/common/components/dataview/ListItem";
import type { CollectionModel } from "@/features/collections/api/models/CollectionModel";
import { useCollections } from "@/features/collections/api/useCollections";
import { CollectionSortOrderOptions } from "@/features/collections/CollectionSortOrder";
import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem, GalleryItemMenu, GalleryItemOverlay, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarInput } from "@/features/common/components/HeaderBarInput";
import { HeaderBarSelector } from "@/features/common/components/HeaderBarSelector";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { Padlock } from "@/features/common/components/Padlock";
import { SmallCollectionDisplay } from "@/features/common/components/SmallCollectionDisplay";
import { useDebouncedState } from "@/lib/common/useDebouncedState";
import { useTitle } from "@/lib/common/useTitle";
import { collectionArts } from "@/lib/media/collectionArts";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { FilterIcon, FolderPlusIcon, HeartCrackIcon, HeartIcon, ImportIcon, SortAscIcon, Trash2Icon, TrophyIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator } from "@/ds/shadcn/components/ui/dropdown-menu";
import { useCollectionDelete } from "@/features/collections/api/useCollectionDelete";
import { useCollectionUpdate } from "@/features/collections/api/useCollectionUpdate";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { StatePanel } from "@/ds/custom/StatePanel";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { ModalCreateCollection } from "@/features/collections/components/ModalCreateCollection";
import { Link } from "react-router";

export const PageCollections: FC = () => {
	useTitle("Collections");

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "Collections" }
	], []);

	const { state, dispatchSetCollectionSortOrder, dispatchSetCollectionFilterQuery } = useUserPreferencesState();

	const [debouncedFilterQuery, filterQuery, setFilterQuery] = useDebouncedState(state.collectionFilterQuery, 500);
	useEffect(() => dispatchSetCollectionFilterQuery(debouncedFilterQuery), [debouncedFilterQuery]);

	const collections = useCollections(state.collectionSortOrder, state.collectionFilterQuery);

	const [collectionIdToDelete, setCollectionIdToDelete] = useState<string | null>(null);
	const [createOpen, setCreateOpen] = useState(false);
	const collectionDelete = useCollectionDelete();
	useNotifyMutationSuccess(collectionDelete, "Collection Deleted", "The collection was deleted successfully.");

	useEffect(() => {
		if (!collectionDelete.isSuccess) {
			return;
		}

		setCollectionIdToDelete(null);
	}, [collectionDelete.isSuccess]);

	return (
		<>
			<HeaderBar title="Collections" description="Manage your collections" breadcrumb={breadcrumb} />
			<HeaderBarTools>
				<HeaderBarInput
					value={filterQuery}
					onChange={setFilterQuery}
					placeholder="Filter name"
					icon={FilterIcon} />

				<HeaderBarSelector
					options={CollectionSortOrderOptions}
					value={state.collectionSortOrder}
					onChange={dispatchSetCollectionSortOrder}
					placeholder="Sort"
					icon={SortAscIcon} />

				<Link to="./csv-import">
					<Button variant="outline" className="w-full @md:w-24">
						<ImportIcon />
						Import
					</Button>
				</Link>

				<Button className="@md:w-24" variant="outline" onClick={() => setCreateOpen(true)}>
					<FolderPlusIcon />
					Create
				</Button>
			</HeaderBarTools>

			<DataView
				forQuery={collections}
				getKey={x => x.collectionId}
				listViewHeaderLabels={["Collection", "Invested", "Gain", "Market Value", "", ""]}
				listViewHeaderClassNames={["min-w-0", "w-30 @2xl:w-48", "w-46 @2xl:w-64", "w-30 @2xl:w-48", "w-16", "w-16"]}
				listComponent={PageCollectionsDataViewListItem}
				galleryComponent={PageCollectionsDataViewGalleryItem}
				additionalProps={{ onDelete: setCollectionIdToDelete}}
				galleryMinWidth={208}>

				<StatePanel
					icon={FolderPlusIcon}
					title="Collections"
					description="Collections let you organise and track the value of your cards.">
					<Button variant="outline" onClick={() => setCreateOpen(true)}>
						Create Collection
					</Button>
				</StatePanel>
			</DataView>

			<ModalConfirm
				title="Delete Collection"
				description="Are you sure you want to delete this collection?  This action is irreversible."
				confirmText="Delete"
				confirmDestructive
				isLoading={collectionDelete.isPending}
				error={collectionDelete.error}
				open={!!collectionIdToDelete}
				onOpenChange={open => {
					if (!open) {
						setCollectionIdToDelete(null);
						collectionDelete.reset();
					}
				}}
				onConfirm={() => collectionIdToDelete && collectionDelete.mutate(collectionIdToDelete)} />

			<ModalCreateCollection
				open={createOpen}
				onOpenChange={setCreateOpen} />
		</>
	);
};

interface PageCollectionsDataViewProps extends CollectionModel {
	onDelete: (collectionId: string) => void;
}

const PageCollectionsDataViewListItem: FC<PageCollectionsDataViewProps> = props => {
	const {
		collectionId,
		name,
		description,
		image,
		marketValue,
		invested,
		gain,
		pinned,
		isGoalCollection,
		onDelete
	} = props;

	const collectionUpdate = useCollectionUpdate();

	const onPinToggle = useCallback(() => {
		collectionUpdate.mutate({
			id: props.collectionId,
			requestModel: {
				...props,
				pinned: !pinned
			}
		});
	}, [pinned]);

	return (
		<ListItem className="relative hover:bg-background-highlight dark:even:hover:bg-background-highlight transition-colors duration-150" isLoading={collectionUpdate.isPending} error={collectionUpdate.error}>
			<TableCell>
				<Link to={`/collections/${collectionId}`} className="after:absolute after:inset-0">
					<SmallCollectionDisplay name={name} description={description} image={image} />
				</Link>
			</TableCell>
			<TableCell>
				<Padlock on={invested}>
					{
						x => <MoneyDisplay mode="Standard" money={x} />
					}
				</Padlock>
			</TableCell>
			<TableCell>
				<Padlock on={invested && gain ? [invested, gain] : null}>
					{
						([x, y]) => <MoneyDisplay mode="DeltaPercentage" money={y} originalValue={x.value} />
					}
				</Padlock>
			</TableCell>
			<TableCell>
				<MoneyDisplay mode="Standard" money={marketValue} />
			</TableCell>
			<TableCell>
				<div className="flex items-center gap-2">
					{
						pinned &&
						<HeartIcon className="size-4 text-destructive" />
					}
					{
						isGoalCollection &&
						<TrophyIcon className="size-4 text-yellow-500" />
					}
				</div>
			</TableCell>
			<TableCell className="relative">
				<ListItemMenu>
					<PageCollectionsDataViewItemMenu {...props} onPinToggle={onPinToggle} onDelete={() => onDelete(props.collectionId)} />
				</ListItemMenu>
			</TableCell>
		</ListItem>
	);
};

const PageCollectionsDataViewGalleryItem: FC<PageCollectionsDataViewProps> = props => {
	const {
		collectionId,
		name,
		description,
		image,
		marketValue,
		invested,
		gain,
		pinned,
		isGoalCollection,
		onDelete
	} = props;

	const collectionArt = useMemo(() => collectionArts[image], [image]);

	const collectionUpdate = useCollectionUpdate();

	const onPinToggle = useCallback(() => {
		collectionUpdate.mutate({
			id: props.collectionId,
			requestModel: {
				...props,
				pinned: !pinned
			}
		});
	}, [pinned]);

	return (
		<GalleryItem imageLink={`/collections/${collectionId}`} isLoading={collectionUpdate.isPending} error={collectionUpdate.error} className="aspect-402/192 @sm:aspect-402/331" title={name} description={description} imageUrl={collectionArt} isProductImage={false}>
			<GalleryItemMenu>
				<PageCollectionsDataViewItemMenu {...props} onPinToggle={onPinToggle} onDelete={() => onDelete(props.collectionId)} />
			</GalleryItemMenu>
			<GalleryItemOverlay className="p-1">
				{
					(pinned || isGoalCollection) &&
					<div className="flex items-center gap-2">
						{
							pinned &&
							<div className="bg-background/60 rounded-sm p-1">
								<HeartIcon className="size-4 text-destructive" />
							</div>
						}
						{
							isGoalCollection &&
							<div className="bg-background/60 rounded-sm p-1">
								<TrophyIcon className="size-4 text-yellow-500" />
							</div>
						}
					</div>
				}
			</GalleryItemOverlay>
			<GalleryItemProperty label="Market Price">
				<MoneyDisplay mode="Standard" money={marketValue} />
			</GalleryItemProperty>
			<GalleryItemProperty label="Gain">
				<Padlock on={invested && gain ? [invested, gain] : null}>
					{
						([x, y]) => <MoneyDisplay mode="DeltaPercentage" money={y} originalValue={x.value} />
					}
				</Padlock>
			</GalleryItemProperty>
		</GalleryItem>
	);
};

interface PageCollectionsDataViewItemMenuProps extends CollectionModel {
	onPinToggle: () => void;
	onDelete: () => void;
}

const PageCollectionsDataViewItemMenu: FC<PageCollectionsDataViewItemMenuProps> = props => {
	const {
		pinned,
		onPinToggle,
		onDelete
	} = props;

	const pinText = pinned ? "Unpin" : "Pin";
	const pinIcon = pinned ? <HeartCrackIcon /> : <HeartIcon />;

	return (
		<DropdownMenuGroup>
			<DropdownMenuItem onClick={onPinToggle}>
				{pinIcon}
				{pinText}
			</DropdownMenuItem>
			<DropdownMenuSeparator />
			<DropdownMenuItem variant="destructive" onClick={onDelete}>
				<Trash2Icon />
				Delete
			</DropdownMenuItem>
		</DropdownMenuGroup>
	);
};
